import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Send, Bot, User, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiAssistantService } from '../services/ai-assistant.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface AIAssistantProps {
  onBack: () => void;
}

interface Message {
  id: string;
  question: string;
  answer: string;
  timestamp: Date;
}

export function AIAssistant({ onBack }: AIAssistantProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch chat history
  const {
    data: historyData,
    isLoading: historyLoading,
    error: historyError,
  } = useQuery({
    queryKey: ['ai-chat-history', user?.id],
    queryFn: () => aiAssistantService.getHistory({ limit: 50 }),
    enabled: !!user,
  });

  const historyMessages: Message[] =
    historyData?.map((msg: any) => ({
      id: msg.id,
      question: msg.question,
      answer: msg.answer,
      timestamp: new Date(msg.createdAt || msg.timestamp),
    })) || [];

  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const allMessages = [...historyMessages, ...localMessages];

  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: (question: string) => aiAssistantService.sendMessage({ question }),
    onSuccess: (response, question) => {
      const newMessage: Message = {
        id: Date.now().toString(),
        question,
        answer: response.answer,
        timestamp: new Date(),
      };
      setLocalMessages((prev) => [...prev, newMessage]);
      queryClient.invalidateQueries({ queryKey: ['ai-chat-history'] });
      setInput('');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages]);

  useEffect(() => {
    if (historyError) {
      toast.error(handleApiError(historyError));
    }
  }, [historyError]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedInput = input.trim();
    if (!trimmedInput) return;
    if (trimmedInput.length > 1000) {
      toast.error('Question must not exceed 1000 characters');
      return;
    }

    sendMutation.mutate(trimmedInput);
  };

  if (historyLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 pb-20">
        <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <LoadingSkeleton variant="rectangular" height="2rem" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <LoadingSkeleton variant="rectangular" height="500px" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col pb-20">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
              aria-label="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">AI Health Assistant</h1>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-4xl w-full mx-auto px-6 py-8 flex flex-col">
        <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
              <Bot className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Chat with AI Health Assistant</h2>
              <p className="text-blue-100">
                Ask questions about your health, nutrition, diabetes management, and more
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 bg-white rounded-2xl border-2 border-gray-200 shadow-sm mb-6 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {allMessages.length === 0 ? (
              <div className="text-center py-12">
                <Bot className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Start a conversation with your AI health assistant</p>
                <p className="text-sm text-gray-500 mt-2">Ask about nutrition, exercise, or health tips</p>
              </div>
            ) : (
              allMessages.map((message) => (
                <div key={message.id} className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-dropsugar-green/20 rounded-full">
                      <User className="w-5 h-5 text-dropsugar-green" />
                    </div>
                    <div className="flex-1">
                      <div className="bg-gray-100 rounded-2xl rounded-tl-none p-4">
                        <p className="text-gray-900">{message.question}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {message.timestamp.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-green-100 rounded-full">
                      <Bot className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="bg-dropsugar-green/10 rounded-2xl rounded-tl-none p-4">
                        <p className="text-gray-900 whitespace-pre-wrap">{message.answer}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {message.timestamp.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}

            {sendMutation.isPending && (
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Bot className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="bg-dropsugar-green/10 rounded-2xl rounded-tl-none p-4">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-5 h-5 text-dropsugar-green animate-spin" />
                      <p className="text-gray-600">Thinking...</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200">
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask a question about your health..."
                  rows={2}
                  maxLength={1000}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent resize-none text-gray-900"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {input.length}/1000 characters
                </p>
              </div>
              <button
                type="submit"
                disabled={sendMutation.isPending || !input.trim()}
                className="p-3 bg-dropsugar-green hover:bg-dropsugar-green-dark disabled:bg-gray-400 text-white rounded-xl shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
